import {
  Button,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  makeStyles,
  Paper,
  styled,
  Typography,
  useMediaQuery,
  useTheme
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import StarOutlineRoundedIcon from "@material-ui/icons/StarOutlineRounded";
import StarRoundedIcon from "@material-ui/icons/StarRounded";
import { useQuery } from "@redux-requests/react";
import { find as _find } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Header, Loader, OverflowTip, VideoPreview } from "../../components";
import StepProgressBar from "../../components/StepProgressBar";
import { createFavourite, getCourse, getSurvey } from "../../ducks/actions";
import * as types from "../../ducks/types";
import { useActionRequest, useUserMe } from "../../hooks";
import { ReactComponent as CoursesSvg } from "../../assets/icons/courses.svg";
import SurveyModal from "./SurveyModal";

const useStyles = makeStyles(theme => ({
  completedModulesContainer: {
    marginTop: "2rem",
    marginBottom: "2rem"
  },
  completedModulesLabel: {
    fontWeight: "bold",
    marginLeft: "0.25rem"
  },
  gridContainer: {
    marginTop: "1rem"
  },
  gridRow: {
    marginBottom: "0.5rem"
  },
  gridCell: {
    padding: "1rem"
  },
  gridCellIcon: {
    display: "flex",
    padding: "1rem",
    justifyContent: "center"
  },
  previewCellContainer: {
    [theme.breakpoints.down("xs")]: { flexDirection: "column", textAlign: "center" }
  },
  previewImage: {
    [theme.breakpoints.up("sm")]: { marginRight: "1.5rem" }
  },
  progressCellContainer: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    [theme.breakpoints.down("md")]: { flexDirection: "column" }
  },
  checkIcon: {
    fontSize: "2rem"
  },
  previewImageLabel: {
    fontWeight: "600",
    fontSize: "1.2rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical"
  },
  actionsCellContainer: {
    display: "flex",
    flexDirection: "column",
    width: "9rem"
  },
  mandatoryLabel: {
    marginTop: "1rem",
    color: "red"
  }
}));

const BorderLinearProgress = styled(LinearProgress)(() => ({
  height: 10,
  borderRadius: 5,
  width: "10rem"
}));

export default function CoursePage() {
  const classes = useStyles();
  const intl = useIntl();
  const { courseId } = useParams();
  const { user } = useUserMe();
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down("md"));

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getCourse({ courseId }));
    dispatch(getSurvey({ courseId }));
  }, [dispatch, courseId]);

  const {
    data: { course },
    loading: courseLoading
  } = useQuery({
    type: types.GET_COURSE,
    defaultData: { course: {} }
  });

  const {
    data: { survey },
    loading: surveyLoading
  } = useQuery({
    type: types.GET_SURVEY,
    defaultData: { survey: { questions: [] } }
  });

  const [surveyOpen, setSurveyOpen] = useState(false);
  const handleOpen = () => setSurveyOpen(true);

  const handleClose = () => {
    dispatch(getCourse({ courseId }));
    setSurveyOpen(false);
  };

  const blockingModule = useMemo(
    () =>
      course?.course_modules?.find(
        module => module.mandatory && module.completion_percentage < 100
      ),
    [course]
  );

  const totalMandatoryModules = useMemo(
    () => course?.course_modules?.filter(module => module.mandatory)?.length,
    [course]
  );

  const completedMandatoryModules = useMemo(
    () =>
      course?.course_modules?.filter(
        module => module.mandatory && module.completion_percentage === 100
      )?.length,
    [course]
  );

  const createFavouriteRequest = useActionRequest({
    action: createFavourite
  });

  const onCreateFavourite = id => event => {
    event.stopPropagation();
    createFavouriteRequest({ favouritableId: id, favouritableType: "CourseModule" });
  };

  const randomizedOrderSurvey = useMemo(
    () => ({
      ...survey,
      questions: survey?.questions?.map(question => {
        const shuffledAnswers = question.answers;
        for (let i = shuffledAnswers?.length - 1; i > 0; i -= 1) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffledAnswers[i], shuffledAnswers[j]] = [shuffledAnswers[j], shuffledAnswers[i]];
        }
        return { ...question, answers: shuffledAnswers };
      })
    }),
    [survey]
  );

  return (
    <>
      <Loader loading={courseLoading || surveyLoading}>
        <Header icon={CoursesSvg} title={`${course.name}`} helper={`${course.description}`} />

        <Grid className={classes.completedModulesContainer} container alignItems="center">
          <Grid className={classes.gridCell} container item justify="center" xs={12} lg={1}>
            <Typography variant="body2">
              {`${course.total_time} ${intl.formatMessage({
                id: "Pages.Course.TotalHours",
                defaultMessage: "total hours"
              })}
              `}
            </Typography>
          </Grid>
          {!isMd && <Divider orientation="vertical" flexItem style={{ marginRight: "-1px" }} />}
          <Grid className={classes.gridCell} item xs={12} sm={5} md={6}>
            <StepProgressBar
              activeStep={completedMandatoryModules}
              stepNumber={totalMandatoryModules}
              type="course"
            />
          </Grid>
          <Grid className={classes.gridCell} container item justify="center" xs={12} sm={3}>
            <Typography variant="body2">
              {intl.formatMessage({
                id: "Pages.Course.YouCompleted",
                defaultMessage: "You've completed"
              })}
            </Typography>
            <Typography className={classes.completedModulesLabel} variant="body2">
              {`${completedMandatoryModules} ${intl.formatMessage({
                id: "Pages.Course.Of",
                defaultMessage: "of"
              })} ${totalMandatoryModules} ${intl.formatMessage({
                id: "Pages.Course.Sessions",
                defaultMessage: "sessions"
              })}`}
            </Typography>
          </Grid>
          <Grid
            className={classes.gridCell}
            container
            item
            justify="center"
            xs={12}
            sm={4}
            md={3}
            lg={2}
          >
            {!course.certificate ? (
              <Button
                color="primary"
                onClick={handleOpen}
                disabled={completedMandatoryModules !== totalMandatoryModules}
                variant="contained"
              >
                {intl.formatMessage({
                  id: "Pages.Course.TakeTest",
                  defaultMessage: "Take test"
                })}
              </Button>
            ) : (
              <Button
                color="primary"
                disabled={completedMandatoryModules !== totalMandatoryModules}
                variant="contained"
                download
                target="_blank"
                href={course.certificate.pdf.url}
              >
                {intl.formatMessage({
                  id: "Pages.Course.DownloadCertificate",
                  defaultMessage: "Download Certificate"
                })}
              </Button>
            )}
          </Grid>
        </Grid>

        <Grid container className={classes.gridContainer}>
          {course.course_modules?.map(value => {
            return (
              <Paper
                elevation={0}
                key={value.id}
                alignItems="center"
                component={Grid}
                className={classes.gridRow}
                container
                item
              >
                <Grid className={classes.gridCellIcon} item xs={12} sm={1}>
                  {value.is_favourite ? (
                    <StarRoundedIcon className={classes.checkIcon} color="primary" />
                  ) : (
                    <IconButton onClick={onCreateFavourite(value.id)}>
                      <StarOutlineRoundedIcon className={classes.checkIcon} color="primary" />
                    </IconButton>
                  )}
                </Grid>
                <Grid
                  container
                  item
                  wrap="nowrap"
                  alignItems="center"
                  className={`${classes.gridCell} ${classes.previewCellContainer}`}
                  xs={12}
                  sm={8}
                  lg={6}
                >
                  {value.cover_image ? (
                    <img
                      className={classes.previewImage}
                      alt=""
                      width={200}
                      src={value.cover_image}
                    />
                  ) : (
                    <VideoPreview
                      className={classes.previewImage}
                      imageWidth={200}
                      videoUrl={value?.recording?.video_url}
                      thumbUrl={value?.document?.pdf?.preview_url}
                    />
                  )}
                  <OverflowTip
                    textToDisplay={value.title}
                    className={classes.previewImageLabel}
                    textVariant="h6"
                  />
                </Grid>
                <Grid item className={classes.gridCell} xs={12} sm={3}>
                  <div className={classes.progressCellContainer}>
                    <Typography variant="body2">
                      {`${intl.formatMessage({
                        id: "Pages.Course.Viewed",
                        defaultMessage: "viewed"
                      })}: ${value?.completion_percentage}%`}
                    </Typography>
                    <span>
                      <BorderLinearProgress
                        variant="determinate"
                        value={value?.completion_percentage}
                      />
                    </span>
                    {value.completion_percentage === 100 && (
                      <CheckCircleIcon className={classes.checkIcon} color="primary" />
                    )}
                  </div>
                </Grid>
                <Grid className={classes.gridCell} container item justify="center" xs={12} lg={2}>
                  <div className={classes.actionsCellContainer}>
                    <Button
                      color="primary"
                      disabled={
                        value?.position > blockingModule?.position &&
                        !_find(value.teachers, teacher => teacher.id === user.profile?.id)
                      }
                      variant="contained"
                      component={RouterLink}
                      to={`/learn/course_modules/${value?.id}`}
                    >
                      {intl.formatMessage({
                        id: "Pages.Course.GoToModule",
                        defaultMessage: "Go to module"
                      })}
                    </Button>
                    {value?.mandatory && (
                      <Typography className={classes.mandatoryLabel} variant="body2">
                        *&nbsp;
                        {intl.formatMessage({
                          id: "Pages.Course.RequiredModule",
                          defaultMessage: "Required module"
                        })}
                      </Typography>
                    )}
                  </div>
                </Grid>
              </Paper>
            );
          })}
        </Grid>
        <SurveyModal survey={randomizedOrderSurvey} isOpen={surveyOpen} handleClose={handleClose} />
      </Loader>
    </>
  );
}
